import {
  ROTULO_DO_STATUS_DE_SINISTRO,
  ROTULO_DO_TIPO_DE_SINISTRO,
  STATUS_DE_SINISTRO,
  type StatusDeSinistro,
  TIPOS_DE_SINISTRO,
  type TipoDeSinistro,
  atualizarSinistroSchema,
  formatarPlaca,
  registrarSinistroSchema,
  uuidSchema,
} from '@locaveri/shared'
import { ImageOff, ImagePlus, LoaderCircle, X } from 'lucide-react'
import { type ReactElement, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Campo } from '@/components/form/campo'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useUrlsDeLeitura } from '@/features/arquivos/url-de-leitura'
import { identificacaoDoVeiculo } from '@/features/frota/api'
import { useFrotaParaSelecao } from '@/features/multas/api'
import { paraInstante } from '@/features/precos/dialogo-de-bloqueio'
import { errorMessage } from '@/lib/api'
import { formatarDataHora } from '@/lib/datas'
import { deCampoDeReaisParaWire, paraCampoDeReais } from '@/lib/dinheiro'
import { aplicarErrosDaApi, aplicarErrosDoZod, validarComSeInformado } from '@/lib/formulario'
import { cn } from '@/lib/utils'

import {
  type CorpoDeSinistro,
  type Sinistro,
  useAtualizarSinistro,
  useEnviarFotoDeSinistro,
  useRegistrarSinistro,
} from './api'

/**
 * Diálogo de sinistro — registro e acompanhamento no mesmo formulário.
 *
 * ---------------------------------------------------------------------------
 * DOIS MODOS, DOIS CONTRATOS
 * ---------------------------------------------------------------------------
 * Sem `sinistro`, o diálogo REGISTRA (`registrarSinistroSchema`): veículo,
 * reserva opcional, tipo, data, descrição e o resto. Com `sinistro`, ele
 * ATUALIZA o andamento (`atualizarSinistroSchema`): status, BO, seguradora,
 * franquia, responsabilidade e observações — o fato em si (carro, tipo, data,
 * o que aconteceu) é registro histórico e não se edita, só se lê no topo.
 *
 * ---------------------------------------------------------------------------
 * A RESERVA É DIGITADA AQUI (AO CONTRÁRIO DA MULTA)
 * ---------------------------------------------------------------------------
 * Sinistro nem sempre acontece em locação; quando acontece, quem sabe a
 * reserva é quem está com o boletim na mão. O serviço confere que a reserva
 * apontada teve ESTE carro NAQUELA data — o vínculo errado volta como 422, e
 * o 422 vira mensagem no formulário.
 */

const CAMPOS = [
  'vehicleId',
  'reservationId',
  'tipo',
  'dataDoSinistro',
  'descricao',
  'boletimDeOcorrencia',
  'seguradoraAcionada',
  'franquiaDaApolice',
  'responsabilidadeDoCliente',
  'status',
  'observacoes',
] as const

interface FormularioDeSinistro {
  vehicleId: string
  /** Opcional — vazio quando o sinistro aconteceu fora de locação. */
  reservationId: string
  tipo: TipoDeSinistro
  /** Hora de parede do `datetime-local`; vira instante com fuso no envio. */
  dataDoSinistro: string
  descricao: string
  boletimDeOcorrencia: string
  /**
   * Os booleanos não são string: `z.boolean()` não faz coerção, e `"false"`
   * chegaria no contrato como verdadeiro — a seguradora apareceria acionada
   * sozinha e a franquia viraria cobrança.
   */
  seguradoraAcionada: boolean
  /** Reais digitados (`"5.000,00"`), convertidos para centavos no envio. */
  franquiaDaApolice: string
  responsabilidadeDoCliente: boolean
  /** Só no modo de atualização. */
  status: StatusDeSinistro
  observacoes: string
}

const TIPO_PADRAO: TipoDeSinistro = TIPOS_DE_SINISTRO[0]
const STATUS_PADRAO: StatusDeSinistro = STATUS_DE_SINISTRO[0]

function valoresIniciais(sinistro: Sinistro | undefined): FormularioDeSinistro {
  return {
    vehicleId: sinistro?.vehicleId ?? '',
    reservationId: sinistro?.reservationId ?? '',
    tipo: sinistro?.tipo ?? TIPO_PADRAO,
    dataDoSinistro: '',
    descricao: sinistro?.descricao ?? '',
    boletimDeOcorrencia: sinistro?.boletimDeOcorrencia ?? '',
    seguradoraAcionada: sinistro?.seguradoraAcionada ?? false,
    franquiaDaApolice: paraCampoDeReais(sinistro?.franquiaDaApolice),
    responsabilidadeDoCliente: sinistro?.responsabilidadeDoCliente ?? false,
    status: sinistro?.status ?? STATUS_PADRAO,
    observacoes: sinistro?.observacoes ?? '',
  }
}

/** O teto do contrato (`registrarSinistroSchema.fotos`). */
const MAXIMO_DE_FOTOS = 30

/** Uma foto já no storage, com a miniatura local para conferência antes de salvar. */
interface FotoNova {
  /** A chave devolvida pelo upload assinado — é o que vai no corpo do registro. */
  readonly chave: string
  /** objectURL do arquivo local (o storage não serve GET anônimo). */
  readonly urlLocal: string
}

/**
 * As fotos de um sinistro JÁ registrado — só leitura: o fato não se edita, e
 * `atualizarSinistroSchema` nem aceita `fotos`. O bucket é privado, então cada
 * chave vira URL assinada de curta duração via `useUrlsDeLeitura`.
 */
function FotosDoSinistro({ chaves }: { readonly chaves: readonly string[] }): ReactElement {
  const { urlPorChave, carregando } = useUrlsDeLeitura(chaves)

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-foreground">Fotos do registro</p>

      <ul className="grid grid-cols-4 gap-2 sm:grid-cols-6">
        {chaves.map((chave) => {
          const url = urlPorChave.get(chave)

          if (url === undefined) {
            return (
              <li key={chave}>
                {carregando ? (
                  <div
                    aria-hidden="true"
                    className="h-16 w-full animate-pulse rounded-md border border-border
                      bg-elevated"
                  />
                ) : (
                  <div
                    title="Não foi possível carregar esta foto"
                    className="flex h-16 w-full items-center justify-center rounded-md border
                      border-border bg-elevated text-fg-muted"
                  >
                    <ImageOff className="size-4" aria-hidden="true" />
                  </div>
                )}
              </li>
            )
          }

          return (
            <li key={chave}>
              <a href={url} target="_blank" rel="noreferrer">
                <img
                  src={url}
                  alt="Foto do sinistro"
                  className="h-16 w-full rounded-md border border-border object-cover"
                />
              </a>
            </li>
          )
        })}
      </ul>

      <p className="text-xs text-fg-muted">
        As fotos fazem parte do registro do fato e não se editam — como o carro, o tipo e a data.
      </p>
    </div>
  )
}

export interface DialogoDeSinistroProps {
  readonly aberto: boolean
  readonly aoFechar: () => void
  /** `undefined` registra; preenchido atualiza o andamento. */
  readonly sinistro?: Sinistro | undefined
}

export function DialogoDeSinistro({
  aberto,
  aoFechar,
  sinistro,
}: DialogoDeSinistroProps): ReactElement {
  const editando = sinistro !== undefined
  const [erroGeral, setErroGeral] = useState<string | null>(null)

  const frota = useFrotaParaSelecao()
  const registrar = useRegistrarSinistro()
  const atualizar = useAtualizarSinistro()
  const enviarFoto = useEnviarFotoDeSinistro()

  const [fotos, setFotos] = useState<readonly FotoNova[]>([])
  const [enviandoFotos, setEnviandoFotos] = useState(false)

  // As miniaturas usam objectURL do arquivo local (o storage não serve GET
  // anônimo). O array é mutado por push e revogado inteiro no desmonte —
  // revogar a cada troca economizaria bytes e custaria um bug de miniatura.
  const urlsLocais = useRef<string[]>([])
  useEffect(() => {
    const urls = urlsLocais.current

    return () => {
      for (const url of urls) {
        URL.revokeObjectURL(url)
      }
    }
  }, [])

  const {
    register,
    handleSubmit,
    setError,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormularioDeSinistro>({ defaultValues: valoresIniciais(undefined) })

  // O diálogo é montado uma vez e reaproveitado: sem este efeito, abrir a
  // atualização depois do registro mostraria os valores do formulário anterior.
  useEffect(() => {
    if (aberto) {
      setErroGeral(null)
      setFotos([])
      reset(valoresIniciais(sinistro))
    }
  }, [aberto, sinistro, reset])

  const seguradoraAcionada = watch('seguradoraAcionada')
  const responsabilidadeDoCliente = watch('responsabilidadeDoCliente')
  const ocupado = registrar.isPending || atualizar.isPending

  /**
   * Sobe cada arquivo pelo upload assinado e guarda a CHAVE; o corpo do
   * registro só leva as chaves. O que exceder o teto do contrato fica de fora
   * ANTES do upload — subir 40 fotos para o Zod recusar 10 seria desperdício.
   */
  function aoEscolherFotos(lista: FileList | null): void {
    if (lista === null || lista.length === 0) {
      return
    }

    const vagas = MAXIMO_DE_FOTOS - fotos.length
    const arquivos = Array.from(lista).slice(0, Math.max(0, vagas))

    if (arquivos.length < lista.length) {
      toast.warning(`O sinistro aceita até ${String(MAXIMO_DE_FOTOS)} fotos`, {
        description: 'As que excederam o limite não foram enviadas.',
      })
    }

    if (arquivos.length === 0) {
      return
    }

    setEnviandoFotos(true)

    void (async () => {
      try {
        for (const arquivo of arquivos) {
          const envio = await enviarFoto.mutateAsync(arquivo)
          const urlLocal = URL.createObjectURL(arquivo)
          urlsLocais.current.push(urlLocal)

          setFotos((atuais) => [...atuais, { chave: envio.chave, urlLocal }])
        }
      } catch (causa) {
        toast.error('Não foi possível enviar a foto', { description: errorMessage(causa) })
      } finally {
        setEnviandoFotos(false)
      }
    })()
  }

  /** Remove ANTES de salvar — o arquivo órfão no storage é o desfecho barato. */
  function removerFoto(chave: string): void {
    setFotos((atuais) => atuais.filter((foto) => foto.chave !== chave))
  }

  const aoEnviar = handleSubmit((valores) => {
    setErroGeral(null)

    // Franquia vazia é `null` explícito ("não conhecida"), nunca zero — e na
    // atualização o `null` LIMPA um valor gravado, que é o que o campo vazio
    // mostra na tela.
    const franquiaWire =
      valores.franquiaDaApolice.trim().length > 0
        ? deCampoDeReaisParaWire(valores.franquiaDaApolice)
        : null

    if (valores.franquiaDaApolice.trim().length > 0 && franquiaWire === null) {
      setError('franquiaDaApolice', {
        type: 'validate',
        message: 'valor inválido — use o formato 5.000,00',
      })
      return
    }

    const aoDarCerto = (salvo: Sinistro): void => {
      toast.success(editando ? 'Sinistro atualizado' : 'Sinistro registrado', {
        description: editando
          ? `Agora está como “${ROTULO_DO_STATUS_DE_SINISTRO[salvo.status]}”.`
          : salvo.reservationId === null
            ? `${ROTULO_DO_TIPO_DE_SINISTRO[salvo.tipo]} do ${formatarPlaca(salvo.veiculoPlaca)} registrado fora de locação.`
            : `${ROTULO_DO_TIPO_DE_SINISTRO[salvo.tipo]} do ${formatarPlaca(salvo.veiculoPlaca)} vinculado à reserva informada.`,
      })
      aoFechar()
    }

    // O 422 da API (reserva que não teve o carro na data, transição recusada)
    // chega com a mensagem dela e cai no campo certo ou no alerta geral.
    const aoFalhar = (causa: unknown): void => {
      setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS))
    }

    if (sinistro !== undefined) {
      const entrada: CorpoDeSinistro = {
        status: valores.status,
        boletimDeOcorrencia:
          valores.boletimDeOcorrencia.trim().length > 0 ? valores.boletimDeOcorrencia : null,
        seguradoraAcionada: valores.seguradoraAcionada,
        franquiaDaApolice: franquiaWire,
        responsabilidadeDoCliente: valores.responsabilidadeDoCliente,
        observacoes: valores.observacoes.trim().length > 0 ? valores.observacoes : null,
      }

      const validado = atualizarSinistroSchema.safeParse(entrada)

      if (!validado.success) {
        setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
        return
      }

      // `entrada`, e não `validado.data`: o schema transforma a franquia em
      // `bigint`, e `JSON.stringify` de `bigint` lança.
      atualizar.mutate(
        { id: sinistro.id, dados: entrada },
        { onSuccess: aoDarCerto, onError: aoFalhar },
      )
      return
    }

    const instante = paraInstante(valores.dataDoSinistro)

    if (instante === null) {
      setError('dataDoSinistro', {
        type: 'validate',
        message: 'informe a data e a hora completas do sinistro',
      })
      return
    }

    const entrada: CorpoDeSinistro = {
      vehicleId: valores.vehicleId,
      reservationId: valores.reservationId.trim().length > 0 ? valores.reservationId.trim() : null,
      tipo: valores.tipo,
      dataDoSinistro: instante,
      descricao: valores.descricao,
      boletimDeOcorrencia:
        valores.boletimDeOcorrencia.trim().length > 0 ? valores.boletimDeOcorrencia : null,
      // As CHAVES do upload assinado — é o que `registrarSinistroSchema.fotos` valida.
      fotos: fotos.map((foto) => foto.chave),
      seguradoraAcionada: valores.seguradoraAcionada,
      franquiaDaApolice: franquiaWire,
      responsabilidadeDoCliente: valores.responsabilidadeDoCliente,
    }

    const validado = registrarSinistroSchema.safeParse(entrada)

    if (!validado.success) {
      setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
      return
    }

    registrar.mutate(entrada, { onSuccess: aoDarCerto, onError: aoFalhar })
  })

  return (
    <Dialog
      open={aberto}
      onOpenChange={(proximo) => {
        if (!proximo) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editando ? 'Atualizar sinistro' : 'Registrar sinistro'}</DialogTitle>
          <DialogDescription>
            {sinistro === undefined
              ? 'O fato como aconteceu: carro, tipo, data e o boletim. A reserva é opcional — sinistro de pátio e translado acontece fora de locação.'
              : `${ROTULO_DO_TIPO_DE_SINISTRO[sinistro.tipo]} · ${formatarPlaca(sinistro.veiculoPlaca)} · ${formatarDataHora(sinistro.dataDoSinistro)}. O fato não se edita; o que anda é o status, a seguradora e a apuração.`}
          </DialogDescription>
        </DialogHeader>

        <form
          noValidate
          onSubmit={(evento) => {
            void aoEnviar(evento)
          }}
          className="space-y-4"
        >
          {erroGeral === null ? null : (
            <Alert variant="danger">
              <AlertDescription>{erroGeral}</AlertDescription>
            </Alert>
          )}

          {!editando && frota.isError ? (
            <Alert variant="danger">
              <AlertDescription className="flex flex-wrap items-center gap-2">
                Não foi possível carregar a frota: {errorMessage(frota.error)}
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    void frota.refetch()
                  }}
                >
                  Tentar de novo
                </Button>
              </AlertDescription>
            </Alert>
          ) : null}

          {editando ? null : (
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                rotulo="Veículo"
                obrigatorio
                erro={errors.vehicleId?.message}
                className="sm:col-span-2"
              >
                {(campo) => (
                  <Select
                    {...campo}
                    {...register('vehicleId', { required: 'escolha o veículo sinistrado' })}
                    disabled={ocupado || frota.isPending}
                  >
                    <option value="">
                      {frota.isPending ? 'Carregando a frota…' : 'Escolha o veículo'}
                    </option>
                    {(frota.data?.itens ?? []).map((veiculo) => (
                      <option key={veiculo.id} value={veiculo.id}>
                        {identificacaoDoVeiculo(veiculo)}
                      </option>
                    ))}
                  </Select>
                )}
              </Campo>

              <Campo rotulo="Tipo" obrigatorio erro={errors.tipo?.message}>
                {(campo) => (
                  <Select {...campo} {...register('tipo')} disabled={ocupado}>
                    {TIPOS_DE_SINISTRO.map((item) => (
                      <option key={item} value={item}>
                        {ROTULO_DO_TIPO_DE_SINISTRO[item]}
                      </option>
                    ))}
                  </Select>
                )}
              </Campo>

              <Campo
                rotulo="Data e hora do sinistro"
                obrigatorio
                erro={errors.dataDoSinistro?.message}
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('dataDoSinistro', {
                      required: 'informe a data e a hora do sinistro',
                    })}
                    type="datetime-local"
                    className="tabular-money"
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Reserva (opcional)"
                erro={errors.reservationId?.message}
                className="sm:col-span-2"
                dica="O id da reserva em que o sinistro aconteceu. Vazio = fora de locação (pátio, translado). A API confere que a reserva teve este carro nesta data."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('reservationId', {
                      validate: validarComSeInformado(uuidSchema),
                    })}
                    placeholder="id da reserva"
                    className="tabular-money"
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo
                rotulo="O que aconteceu"
                obrigatorio
                erro={errors.descricao?.message}
                className="sm:col-span-2"
                dica="Mínimo de 10 caracteres — é o relato que a seguradora e a apuração vão ler."
              >
                {(campo) => (
                  <Textarea
                    {...campo}
                    {...register('descricao', { required: 'descreva o que aconteceu' })}
                    rows={3}
                    maxLength={5000}
                    disabled={ocupado}
                  />
                )}
              </Campo>

              {/* As fotos sobem AGORA (upload assinado, chave guardada) e só
                  entram no sinistro quando o formulário salvar — dá para
                  conferir a miniatura e remover antes de virar registro. */}
              <div className="space-y-2 sm:col-span-2">
                <p className="text-sm font-medium text-foreground">Fotos do sinistro</p>

                {fotos.length === 0 ? null : (
                  <ul className="grid grid-cols-4 gap-2 sm:grid-cols-6">
                    {fotos.map((foto) => (
                      <li key={foto.chave} className="relative">
                        <img
                          src={foto.urlLocal}
                          alt="Foto do sinistro"
                          className="h-16 w-full rounded-md border border-border object-cover"
                        />
                        <button
                          type="button"
                          aria-label="Remover foto"
                          onClick={() => {
                            removerFoto(foto.chave)
                          }}
                          className="absolute -right-1.5 -top-1.5 rounded-full border border-border
                            bg-elevated p-0.5 text-fg-secondary transition-colors
                            hover:text-foreground"
                        >
                          <X className="size-3" aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="flex flex-wrap items-center gap-3">
                  <label
                    className={cn(
                      'flex h-9 w-fit cursor-pointer items-center gap-2 rounded-lg border',
                      'border-border bg-elevated px-3 text-sm font-medium text-foreground',
                      'transition-colors focus-within:ring-2 focus-within:ring-ring',
                      'hover:bg-accent',
                      (enviandoFotos || ocupado || fotos.length >= MAXIMO_DE_FOTOS) &&
                        'pointer-events-none opacity-50',
                    )}
                  >
                    {enviandoFotos ? (
                      <LoaderCircle className="size-4 animate-spin" aria-hidden="true" />
                    ) : (
                      <ImagePlus className="size-4" aria-hidden="true" />
                    )}
                    {fotos.length === 0 ? 'Enviar fotos' : 'Adicionar mais'}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="sr-only"
                      disabled={enviandoFotos || ocupado || fotos.length >= MAXIMO_DE_FOTOS}
                      onChange={(evento) => {
                        aoEscolherFotos(evento.target.files)
                        // Sem zerar, escolher o MESMO arquivo de novo não dispara change.
                        evento.target.value = ''
                      }}
                    />
                  </label>

                  <p className="text-xs tabular-money text-fg-muted">
                    {fotos.length} de {MAXIMO_DE_FOTOS}
                  </p>
                </div>

                <p className="text-xs text-fg-muted">
                  Opcionais, mas valem a apuração inteira: o dano, a posição dos carros, o local.
                  Depois de salvo, o registro não aceita foto nova.
                </p>
              </div>
            </div>
          )}

          {sinistro !== undefined && sinistro.fotos.length > 0 ? (
            <FotosDoSinistro chaves={sinistro.fotos} />
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            {editando ? (
              <Campo
                rotulo="Status"
                obrigatorio
                erro={errors.status?.message}
                dica="Os quatro status transitam livres, inclusive para trás."
              >
                {(campo) => (
                  <Select {...campo} {...register('status')} disabled={ocupado}>
                    {STATUS_DE_SINISTRO.map((item) => (
                      <option key={item} value={item}>
                        {ROTULO_DO_STATUS_DE_SINISTRO[item]}
                      </option>
                    ))}
                  </Select>
                )}
              </Campo>
            ) : null}

            <Campo
              rotulo="Boletim de ocorrência"
              erro={errors.boletimDeOcorrencia?.message}
              dica="O número do BO, quando houver."
            >
              {(campo) => (
                <Input
                  {...campo}
                  {...register('boletimDeOcorrencia')}
                  maxLength={60}
                  placeholder="AB1234-2026"
                  className="tabular-money"
                  disabled={ocupado}
                />
              )}
            </Campo>

            <Campo
              rotulo="Franquia da apólice"
              erro={errors.franquiaDaApolice?.message}
              dica="Em reais. Vazio = ainda não conhecida."
            >
              {(campo) => (
                <Input
                  {...campo}
                  {...register('franquiaDaApolice', {
                    validate: (valor: string) =>
                      valor.trim().length === 0 ||
                      deCampoDeReaisParaWire(valor) !== null ||
                      'valor inválido — use o formato 5.000,00',
                  })}
                  inputMode="decimal"
                  placeholder="0,00"
                  className="tabular-money"
                  disabled={ocupado}
                />
              )}
            </Campo>
          </div>

          <fieldset className="space-y-2" disabled={ocupado}>
            <legend className="text-sm font-medium text-foreground">Seguradora e apuração</legend>

            <label
              className="flex cursor-pointer items-start gap-2 rounded-lg border border-border
                bg-surface p-3 text-sm"
            >
              <Checkbox {...register('seguradoraAcionada')} className="mt-0.5" />
              <span className="min-w-0">
                <span className="block font-medium text-foreground">Seguradora acionada</span>
                <span className="block text-xs text-fg-secondary">
                  Marcado quando o aviso de sinistro foi aberto na apólice do veículo.
                </span>
              </span>
            </label>

            <label
              className="flex cursor-pointer items-start gap-2 rounded-lg border border-border
                bg-surface p-3 text-sm"
            >
              <Checkbox {...register('responsabilidadeDoCliente')} className="mt-0.5" />
              <span className="min-w-0">
                <span className="block font-medium text-foreground">
                  Responsabilidade do cliente
                </span>
                <span className="block text-xs text-fg-secondary">
                  É este campo que autoriza a franquia a virar linha de cobrança no fechamento da
                  reserva. Marque só depois da apuração.
                </span>
              </span>
            </label>
          </fieldset>

          {editando ? (
            <Campo
              rotulo="Observações"
              erro={errors.observacoes?.message}
              dica="O andamento em texto: orçamento da oficina, protocolo da seguradora, decisão da apuração."
            >
              {(campo) => (
                <Textarea
                  {...campo}
                  {...register('observacoes')}
                  rows={3}
                  maxLength={5000}
                  disabled={ocupado}
                />
              )}
            </Campo>
          ) : null}

          {/* Âmbar semântico, nunca o amarelo da marca: isto é aviso. */}
          {seguradoraAcionada && !responsabilidadeDoCliente ? (
            <Alert variant="warning">
              <AlertDescription>
                Seguradora acionada sem responsabilidade atribuída: a franquia registrada NÃO será
                cobrada do cliente no fechamento enquanto a apuração não marcar a responsabilidade.
              </AlertDescription>
            </Alert>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={aoFechar} disabled={ocupado}>
              Cancelar
            </Button>
            {/* Submeter no meio de um upload registraria o sinistro SEM as
                fotos que ainda estão subindo — espera-se o storage terminar. */}
            <Button type="submit" disabled={ocupado || enviandoFotos}>
              {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
              {editando ? 'Salvar andamento' : 'Registrar sinistro'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
